import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../utils/firebase';
import { reportSubscriptionConversion } from '../utils/api';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

export function useSubscription() {
  return useContext(SubscriptionContext);
}

// A missing subscriptions/{uid} doc means the account has never subscribed —
// mirrors the same free/full-access default the Firestore/Storage rules fall
// back to when the doc doesn't exist.
const DEFAULT_SUBSCRIPTION = {
  tier: 'free',
  accessState: 'full',
  maxPeople: 50,
  maxStorageBytes: 104857600,
};

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(DEFAULT_SUBSCRIPTION);

  useEffect(() => {
    if (!user) {
      setSubscription(DEFAULT_SUBSCRIPTION);
      return;
    }
    const unsub = onSnapshot(doc(db, 'subscriptions', user.uid), (snap) => {
      setSubscription(snap.exists() ? { ...DEFAULT_SUBSCRIPTION, ...snap.data() } : DEFAULT_SUBSCRIPTION);
    });
    return unsub;
  }, [user]);

  // Asking the server to report a paid subscription to the advertising platform.
  //
  // Driven off the subscription listener rather than off the subscribe call,
  // because the thing worth waiting for arrives after it. The charge amount is
  // written by the Braintree webhook seconds later, and the platform will not
  // revise a value on a duplicate event, so a purchase reported the instant
  // somebody pays is a purchase whose amount can never be sent.
  //
  // So this fires twice at most, and the second time is the one that matters:
  // once when the account first reads as paid, and again when the amount
  // appears. The server holds the first briefly, waiting for exactly that, and
  // reports without an amount only if the webhook never comes.
  //
  // Keyed on whether the amount is known rather than on a plain flag, which is
  // what makes it fire again on the transition and not on every snapshot.
  const reportedRef = useRef(null);
  useEffect(() => {
    if (!user || subscription.tier !== 'paid') return;
    const amountKnown = typeof subscription.lastChargeCents === 'number';
    const key = `${user.uid}:${amountKnown}`;
    if (reportedRef.current === key) return;
    reportedRef.current = key;
    reportSubscriptionConversion();
  }, [user, subscription.tier, subscription.lastChargeCents]);

  const getClientToken   = () => httpsCallable(functions, 'create_braintree_client_token')();
  // couponCode is the raw string the user typed; the callable resolves it to a
  // Braintree discount id server-side and rejects anything that doesn't match.
  const subscribe        = (nonce, couponCode) => httpsCallable(functions, 'create_subscription')({
    paymentMethodNonce: nonce,
    ...(couponCode ? { couponCode } : {}),
  });
  const validateCoupon   = (code) => httpsCallable(functions, 'validate_coupon')({ code });
  const cancelSubscription = () => httpsCallable(functions, 'cancel_subscription')();
  const getSubscriptionDetails = () => httpsCallable(functions, 'get_subscription_details')();

  const value = {
    subscription,
    tier: subscription.tier,
    accessState: subscription.accessState,
    isPaid: subscription.tier === 'paid',
    isGrace: subscription.accessState === 'grace',
    isBlocked: subscription.accessState === 'blocked',
    // Whether Braintree would still charge this account — which is not the
    // same question as whether they have paid access. cancel_subscription
    // records cancelledAt and deliberately leaves tier and accessState alone so
    // access runs to the end of the paid period, so a cancelled account still
    // reads as a full-access paid tier for up to a month. Account deletion is
    // blocked on billing, not on access, and this is that distinction.
    hasActiveBilling: Boolean(subscription.braintreeSubscriptionId) && !subscription.cancelledAt,
    maxPeople: subscription.maxPeople,
    maxStorageBytes: subscription.maxStorageBytes,
    getClientToken, subscribe, validateCoupon, cancelSubscription, getSubscriptionDetails,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}
